"use client";

import { getCSSVar, getLocaleConstants, useMediaQuery } from "@tosize/ui";
import { type FC, type PropsWithChildren, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "../components/Form/Steps/ShapeDimensions";
import { THICKNESS_STEP_IDENTIFIER } from "../components/Form/Steps/Thickness";
import { useDebounce } from "../hooks/useDebounce";
import { CardContext } from "./CardContext";
import { useConfig } from "./ConfigContext";

export const CardContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const { mode } = useConfig();
	const [openCards, setOpenCards] = useState<string[]>([THICKNESS_STEP_IDENTIFIER]);
	const [invalidCards, setInvalidCards] = useState<string[]>([]);
	const [showVisualisation, setShowVisualisation] = useState(false);
	const isMaxMedium = useMediaQuery({ max: "lg" });
	const LOAD_DELAY = 50;

	const intl = useIntl();
	const localeConstants = getLocaleConstants(intl.locale);
	const { postcode, setActiveDialog } = useConfig();

	const onOpenCardsChange = useCallback(
		(newOpenCards: string[]) => {
			setOpenCards(newOpenCards);

			if (newOpenCards.includes(SHAPE_DIMENSIONS_STEP_IDENTIFIER)) {
				setActiveDialog(postcode ? undefined : "postcode");
			}

			const postCodeRequired = localeConstants.postcode;
			const canShowVisualisation = postCodeRequired ? !!postcode : true;

			setShowVisualisation(
				canShowVisualisation && newOpenCards.includes(SHAPE_DIMENSIONS_STEP_IDENTIFIER),
			);
		},
		[localeConstants.postcode, postcode, setActiveDialog],
	);

	/**
	 * scroll has to be delayed because the card animation takes some time
	 * first we check if the element exist
	 * in mode `add` we need to calulate the element position minus the header height and some padding
	 *   on desktop we also have to take into account that the whole configurator scrolls.
	 * in mode `edit`|`addInDialog` we can use the native scrollIntoView
	 */
	const scrollToElement = useCallback(
		(identifier: string) => {
			setTimeout(() => {
				const element = document.querySelector(identifier) as HTMLElement | undefined;

				if (element) {
					if (mode === "add") {
						const getTop = () => {
							if (isMaxMedium) {
								return element.getBoundingClientRect().top + window.scrollY;
							}

							const mainView = document.getElementById("main-view");
							const startOrdering = document.getElementById("start-ordering");

							if (mainView && startOrdering) {
								const parentOffsetTop = mainView.getBoundingClientRect().top + window.scrollY;
								const y = mainView.offsetHeight - startOrdering.offsetHeight + parentOffsetTop;
								return y + element.offsetTop;
							}
						};

						const top = getTop();

						if (top) {
							const padding = 16;
							const headerHeight = getCSSVar("--header-y");

							window.scrollTo({ top: top - (padding + headerHeight), behavior: "smooth" });
						}
					} else {
						element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
					}
				}
			}, LOAD_DELAY);
		},
		[isMaxMedium, mode],
	);

	const navigateToCard = useCallback(
		(from: string, to: string, scroll?: boolean) => {
			// setOpenCards((prev) => [...prev.filter((card) => card !== from), to]);

			onOpenCardsChange(openCards.filter((card) => card !== from).concat(to));

			if (scroll) {
				scrollToElement(`div[data-card="${to}"]`);
			}
		},
		[scrollToElement, onOpenCardsChange, openCards],
	);

	const [focused, setFocused] = useState(false);
	const onFocus = useDebounce((value) => setFocused(Boolean(value)), 100);

	return (
		<CardContext.Provider
			value={{
				openCards,
				onOpenCardsChange,

				navigateToCard,
				scrollToElement,

				invalidCards,
				setInvalidCards,

				showVisualisation,
				setShowVisualisation,

				focused,
				setFocused,
				onFocus,
			}}
		>
			{children}
		</CardContext.Provider>
	);
};
